/**
 * A per-article-view UUID, shared across multiple trackers (e.g.
 * ArticleScrollDepthTracker and ArticleOutboundClickTracker) so their events
 * all carry the same article_view_id. Held purely in a closure -- no
 * globals, no cookie/localStorage/sessionStorage.
 *
 * One tracker owns generation (calls ensureViewId()/reset()); any other
 * tracker sharing the same session only ever reads via getViewId().
 */

export interface ArticleViewSession {
  ensureViewId(): string;
  getViewId(): string | null;
  reset(): void;
}

export function generateUuid(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  // Fallback for non-secure contexts/older browsers where crypto.randomUUID is
  // unavailable, mirroring the crypto.getRandomValues fallback already used by
  // this repo's Permutive.js.
  const cryptoObj =
    (typeof crypto !== "undefined" && crypto) ||
    (typeof window !== "undefined" && (window as unknown as { msCrypto?: Crypto }).msCrypto);
  const bytes = new Uint8Array(16);
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

export function createArticleViewSession(): ArticleViewSession {
  let viewId: string | null = null;
  return {
    ensureViewId() {
      if (!viewId) viewId = generateUuid();
      return viewId;
    },
    getViewId() {
      return viewId;
    },
    reset() {
      viewId = null;
    },
  };
}

export function isArticleViewSession(
  candidate: unknown
): candidate is ArticleViewSession {
  return (
    !!candidate &&
    typeof (candidate as ArticleViewSession).ensureViewId === "function" &&
    typeof (candidate as ArticleViewSession).getViewId === "function" &&
    typeof (candidate as ArticleViewSession).reset === "function"
  );
}
